// src/modules/accounting/components/ManualJournalModal.jsx
//
// Compose a manual double-entry journal entry and submit it for approval.
// Nothing posts to the ledger here — the backend captures a ManualJournalRequest
// and the Approval Engine posts the real entry (and any fund / cash-drawer / HO-
// bank sync) on approval.

import React, { useEffect, useMemo, useState, useCallback } from 'react';
import {
  Modal, DatePicker, Input, Select, InputNumber, Button, Space, Row, Col,
  Typography, Tag, Alert, Divider, message, Card, Tooltip,
} from 'antd';
import {
  PlusOutlined, DeleteOutlined, BankOutlined, WalletOutlined, InfoCircleOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { getAccounts, fetchFunds, createManualJournal, getOutstandingSmelterSettlements } from '../api';
import DenominationCountForm from '../../treasury/components/DenominationCountForm';

const { Text, Title } = Typography;

const NOTE_VALUES = { note2000: 2000, note500: 500, note200: 200, note100: 100, note50: 50, note20: 20, note10: 10, note5: 5, note2: 2, note1: 1 };
const denomTotal = (c = {}) => Object.entries(NOTE_VALUES).reduce((s, [k, v]) => s + (c[k] || 0) * v, 0);
const fmt = (v) => Number((v || 0).toFixed(2)).toLocaleString('en-IN');
const r2 = (v) => Math.round((Number(v) || 0) * 100) / 100;
const uid = () => Math.random().toString(36).slice(2, 9);

const emptyLine = () => ({
  key: uid(), accountId: null, debit: null, credit: null, narration: '',
  fundId: null, denominations: {}, bankAccount: '', bankReference: '', valueDate: null,
  // Set when this line clears a smelter sale bill. On approval the credit is
  // registered as a payment receipt against that dispatch — this entry IS the
  // payment entry, so nothing extra is posted.
  smelterDispatchId: null, smelterTdsAmount: null,
});

// Built-in TDS Receivable — the account a smelter's deduction is debited to.
const DEFAULT_TDS_CODE = '1340';

export default function ManualJournalModal({ open, onClose, onSubmitted }) {
  const [date, setDate] = useState(dayjs());
  const [narration, setNarration] = useState('');
  const [lines, setLines] = useState([emptyLine(), emptyLine()]);
  const [accounts, setAccounts] = useState([]);
  const [funds, setFunds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [denomEditor, setDenomEditor] = useState(null); // { key } of line being edited
  const [dispatches, setDispatches] = useState([]);      // billed-but-unpaid smelter dispatches

  const reset = useCallback(() => {
    setDate(dayjs()); setNarration(''); setLines([emptyLine(), emptyLine()]);
  }, []);

  useEffect(() => {
    if (!open) return;
    reset();
    (async () => {
      setLoading(true);
      try {
        const [accRes, fundRes, dispRes] = await Promise.all([
          getAccounts({ isActive: true }),
          fetchFunds(),
          // Never let a smelter-side hiccup block composing an ordinary entry.
          getOutstandingSmelterSettlements().catch(() => ({ data: [] })),
        ]);
        const accList = accRes?.data || accRes?.accounts || accRes || [];
        const fundList = fundRes?.data || fundRes || [];
        setAccounts((accList || []).filter((a) => a.isActive !== false));
        setFunds((fundList || []).filter((f) => f.isActive !== false));
        setDispatches(dispRes?.data || []);
      } catch (err) {
        message.error(err?.response?.data?.message || err.message || 'Failed to load accounts / funds');
      } finally {
        setLoading(false);
      }
    })();
  }, [open, reset]);

  const accountsById = useMemo(() => {
    const m = {}; accounts.forEach((a) => { m[a._id] = a; }); return m;
  }, [accounts]);

  // Funds whose linked GL account matches a given account code (candidates for sync).
  const fundsForAccount = useCallback((account) => {
    if (!account || account.type !== 'ASSET') return [];
    return funds.filter((f) => f.linkedAccountCode && String(f.linkedAccountCode) === String(account.code));
  }, [funds]);

  const patchLine = (key, patch) =>
    setLines((prev) => prev.map((l) => (l.key === key ? { ...l, ...patch } : l)));

  const dispatchById = useMemo(() => {
    const m = {}; dispatches.forEach((d) => { m[d._id] = d; }); return m;
  }, [dispatches]);

  const tdsAccountFor = useCallback((dispatch) => (
    dispatch?.tdsAccount?._id
    || accounts.find((a) => a.code === DEFAULT_TDS_CODE)?._id
    || null
  ), [accounts]);

  /**
   * Keep a smelter settlement and the lines that fund it in step.
   *
   * The tagged line is the one that CREDITS the dispatch's debtors account — that
   * is what clears the receivable, and its amount is what the dispatch is settled
   * by. Its counterparts are written for you: a debit for the cash that actually
   * arrived and, when the smelter withheld tax, a debit to the TDS account. Both
   * stay editable — they are a starting point, not a lock.
   */
  const syncSmelterLines = useCallback((lineKey, patch) => {
    setLines((prev) => {
      let next = prev.map((l) => ({ ...l }));
      const i = next.findIndex((l) => l.key === lineKey);
      if (i < 0) return prev;
      const line = next[i];

      const dispatchId = patch.dispatchId !== undefined ? patch.dispatchId : line.smelterDispatchId;

      // Untagging drops the lines that were written for this settlement.
      if (!dispatchId) {
        next[i] = { ...line, smelterDispatchId: null, smelterTdsAmount: null };
        next = next.filter((l) => l.autoFor !== lineKey);
        while (next.length < 2) next.push(emptyLine());
        return next;
      }

      const d = dispatchById[dispatchId];
      const credit = patch.credit !== undefined
        ? patch.credit
        : (line.credit != null ? line.credit : (d?.availableToSettle ?? null));
      const tds = patch.tds !== undefined ? patch.tds : line.smelterTdsAmount;

      const amount = Number(credit) || 0;
      const tdsAmt = Math.min(Number(tds) || 0, amount);
      const cashAmt = r2(amount - tdsAmt);

      next[i] = {
        ...line,
        smelterDispatchId: dispatchId,
        smelterTdsAmount: tds ?? null,
        accountId: d?.debtorAccount?._id || line.accountId,
        credit: credit ?? null,
        debit: null,
        narration: line.narration || (d ? `Settles ${d.dispatchId} — invoice ${d.invoiceNo}` : ''),
      };

      // The money that actually arrived — account left for you to pick.
      let bi = next.findIndex((l) => l.autoFor === lineKey && l.autoRole === 'BANK');
      if (bi < 0) {
        bi = next.findIndex((l, idx) => idx !== i && !l.autoFor && !l.accountId && !l.debit && !l.credit);
        if (bi >= 0) next[bi] = { ...next[bi], autoFor: lineKey, autoRole: 'BANK' };
      }
      if (bi < 0) {
        next.push({ ...emptyLine(), autoFor: lineKey, autoRole: 'BANK' });
        bi = next.length - 1;
      }
      next[bi] = {
        ...next[bi],
        debit: cashAmt || null,
        credit: null,
        narration: next[bi].narration || (d ? `Received from ${d.smelterName || 'smelter'} — ${d.dispatchId}` : ''),
      };

      // Tax the smelter withheld — no cash for it, but it still clears the debtor.
      let ti = next.findIndex((l) => l.autoFor === lineKey && l.autoRole === 'TDS');
      if (tdsAmt > 0) {
        if (ti < 0) {
          next.push({
            ...emptyLine(),
            autoFor: lineKey,
            autoRole: 'TDS',
            accountId: tdsAccountFor(d),
            narration: d ? `TDS withheld by smelter — ${d.dispatchId}` : 'TDS withheld by smelter',
          });
          ti = next.length - 1;
        }
        next[ti] = {
          ...next[ti],
          accountId: next[ti].accountId || tdsAccountFor(d),
          debit: tdsAmt,
          credit: null,
        };
      } else if (ti >= 0) {
        next = next.filter((_, idx) => idx !== ti);
      }

      while (next.length < 2) next.push(emptyLine());
      return next;
    });
  }, [dispatchById, tdsAccountFor]);

  const addLine = () => setLines((prev) => [...prev, emptyLine()]);
  const removeLine = (key) => setLines((prev) => (prev.length <= 2 ? prev : prev.filter((l) => l.key !== key)));

  // ── Live totals ──
  const totalDebit = lines.reduce((s, l) => s + (Number(l.debit) || 0), 0);
  const totalCredit = lines.reduce((s, l) => s + (Number(l.credit) || 0), 0);
  const diff = Number((totalDebit - totalCredit).toFixed(2));
  const balanced = diff === 0 && totalDebit > 0;

  // ── Per-line validation for fund sync ──
  const lineIssues = (l) => {
    const debit = Number(l.debit) || 0;
    const credit = Number(l.credit) || 0;
    const amount = debit || credit;
    const issues = [];
    if (l.accountId && debit > 0 && credit > 0) issues.push('A line cannot be both debit and credit');

    if (l.smelterDispatchId) {
      const d = dispatchById[l.smelterDispatchId];
      if (credit <= 0 || debit > 0) {
        issues.push('A smelter settlement must CREDIT the debtors account — it clears what the smelter owes');
      }
      if (d) {
        if (credit > d.availableToSettle + 0.01) {
          issues.push(`At most ₹${fmt(d.availableToSettle)} can be settled against ${d.dispatchId}`);
        }
        if (d.debtorAccount?._id && l.accountId && String(l.accountId) !== String(d.debtorAccount._id)) {
          issues.push(`${d.dispatchId} was billed to ${d.debtorAccount.code} — ${d.debtorAccount.name}; credit that account`);
        }
        const tds = Number(l.smelterTdsAmount) || 0;
        if (tds > credit + 0.01) issues.push('TDS cannot be more than the amount being settled');
      }
    }

    if (!l.fundId) return issues;
    const fund = funds.find((f) => f._id === l.fundId);
    if (!fund) return issues;
    if (fund.isIBCTCashFund) {
      const t = denomTotal(l.denominations);
      if (t !== Number(amount.toFixed(2))) issues.push(`Denomination total (₹${fmt(t)}) must equal line amount (₹${fmt(amount)})`);
    }
    return issues;
  };

  // Two lines settling the same dispatch would be ambiguous to register.
  const duplicateDispatchIssues = (() => {
    const seen = new Set(); const dupes = new Set();
    lines.forEach((l) => {
      if (!l.smelterDispatchId) return;
      if (seen.has(l.smelterDispatchId)) dupes.add(l.smelterDispatchId);
      seen.add(l.smelterDispatchId);
    });
    return [...dupes].map((id) => `${dispatchById[id]?.dispatchId || 'A dispatch'} is settled on two lines — put the whole settlement on one line`);
  })();

  const allLineIssues = [...lines.flatMap(lineIssues), ...duplicateDispatchIssues];
  const activeLines = lines.filter((l) => l.accountId && ((Number(l.debit) || 0) > 0 || (Number(l.credit) || 0) > 0));
  const canSubmit = balanced && activeLines.length >= 2 && allLineIssues.length === 0 && !submitting;

  const submit = async () => {
    const payloadLines = activeLines.map((l) => {
      const fund = l.fundId ? funds.find((f) => f._id === l.fundId) : null;
      const out = {
        accountId: l.accountId,
        debit: Number(l.debit) || 0,
        credit: Number(l.credit) || 0,
        narration: l.narration || '',
      };
      if (l.smelterDispatchId) {
        out.smelterDispatchId = l.smelterDispatchId;
        out.smelterTdsAmount = Number(l.smelterTdsAmount) || 0;
      }
      if (fund) {
        out.fundId = fund._id;
        if (fund.isIBCTCashFund) out.denominations = l.denominations;
        if (fund.isCentralFund && (Number(l.debit) || 0) > 0) {
          out.bankAccount = l.bankAccount || '';
          out.bankReference = l.bankReference || '';
          out.valueDate = l.valueDate ? l.valueDate.toISOString() : null;
        }
      }
      return out;
    });

    setSubmitting(true);
    try {
      const res = await createManualJournal({
        date: date ? date.toISOString() : undefined,
        narration,
        lines: payloadLines,
      });
      message.success(res.message || 'Manual journal submitted');
      onSubmitted?.();
      onClose?.();
    } catch (err) {
      message.error(err?.response?.data?.message || err.message || 'Submission failed');
    } finally {
      setSubmitting(false);
    }
  };

  const accountOptions = accounts.map((a) => ({
    value: a._id,
    label: `${a.code} — ${a.name}`,
    type: a.type,
  }));

  return (
    <>
      <Modal
        open={open}
        onCancel={onClose}
        width={980}
        title="New Manual Journal Entry"
        okText={submitting ? 'Submitting…' : 'Submit for Approval'}
        okButtonProps={{ disabled: !canSubmit, loading: submitting }}
        onOk={submit}
        destroyOnClose
      >
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
          message="This entry is routed through the Approval Engine."
          description="On approval it posts a real double-entry journal (reflected in Trial Balance, P&L, Balance Sheet, Ledgers) and syncs any attached fund's balance, cash-drawer notes, or HO-bank ledger — all atomically."
        />

        <Row gutter={12} style={{ marginBottom: 8 }}>
          <Col span={7}>
            <Text type="secondary">
              Transaction Date{' '}
              <Tooltip title="When the activity actually happened. This is the date every report uses — Trial Balance, P&L, Balance Sheet and the ledgers — so back-date it to land the entry in the right period.">
                <InfoCircleOutlined />
              </Tooltip>
            </Text>
            <DatePicker value={date} onChange={setDate} format="DD/MM/YYYY" allowClear={false} style={{ width: '100%' }} />
          </Col>
          <Col span={7}>
            <Text type="secondary">
              Entry Date{' '}
              <Tooltip title="When the entry is booked into the ledger. Stamped automatically at the moment approval posts it, so it stays a truthful record of when the books were touched.">
                <InfoCircleOutlined />
              </Tooltip>
            </Text>
            <Input
              disabled
              value={date && !date.isSame(dayjs(), 'day')
                ? 'On approval (back-dated entry)'
                : 'On approval'}
            />
          </Col>
          <Col span={10}>
            <Text type="secondary">Narration</Text>
            <Input value={narration} onChange={(e) => setNarration(e.target.value)} placeholder="Purpose of this entry" />
          </Col>
        </Row>
        {date && !date.isSame(dayjs(), 'day') && (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 8 }}
            message={`Back-dated to ${date.format('DD/MM/YYYY')} — it will report in that period, while the entry date records when it was actually booked.`}
          />
        )}

        <Divider orientation="left" style={{ fontSize: 13, margin: '12px 0' }}>Lines</Divider>

        <Space direction="vertical" size={8} style={{ width: '100%' }}>
          {lines.map((l, idx) => {
            const acct = accountsById[l.accountId];
            const candidates = fundsForAccount(acct);
            const fund = l.fundId ? funds.find((f) => f._id === l.fundId) : null;
            const amount = (Number(l.debit) || 0) || (Number(l.credit) || 0);
            const isInflow = (Number(l.debit) || 0) > 0;
            const issues = lineIssues(l);
            return (
              <Card key={l.key} size="small" bodyStyle={{ padding: 10 }} style={issues.length ? { borderColor: '#ffccc7' } : undefined}>
                <Row gutter={8} align="middle">
                  <Col flex="auto">
                    <Select
                      showSearch
                      placeholder={`Account (line ${idx + 1})`}
                      value={l.accountId}
                      onChange={(v) => patchLine(l.key, { accountId: v, fundId: null })}
                      options={accountOptions}
                      optionFilterProp="label"
                      style={{ width: '100%' }}
                      loading={loading}
                    />
                  </Col>
                  <Col flex="140px">
                    <InputNumber
                      placeholder="Debit" min={0} controls={false} style={{ width: '100%' }}
                      value={l.debit}
                      onChange={(v) => patchLine(l.key, { debit: v, credit: v ? null : l.credit })}
                      formatter={(v) => (v ? `₹ ${v}` : '')}
                    />
                  </Col>
                  <Col flex="140px">
                    <InputNumber
                      placeholder="Credit" min={0} controls={false} style={{ width: '100%' }}
                      value={l.credit}
                      max={l.smelterDispatchId ? dispatchById[l.smelterDispatchId]?.availableToSettle : undefined}
                      onChange={(v) => (l.smelterDispatchId
                        ? syncSmelterLines(l.key, { credit: v })
                        : patchLine(l.key, { credit: v, debit: v ? null : l.debit }))}
                      formatter={(v) => (v ? `₹ ${v}` : '')}
                    />
                  </Col>
                  <Col flex="30px">
                    <Button type="text" danger icon={<DeleteOutlined />} disabled={lines.length <= 2} onClick={() => removeLine(l.key)} />
                  </Col>
                </Row>

                <Row gutter={8} style={{ marginTop: 6 }}>
                  <Col flex="auto">
                    <Input
                      size="small" placeholder="Line narration (optional)"
                      value={l.narration} onChange={(e) => patchLine(l.key, { narration: e.target.value })}
                    />
                  </Col>
                  {candidates.length > 0 && (
                    <Col flex="320px">
                      <Select
                        size="small" allowClear placeholder="Sync a fund (optional)"
                        style={{ width: '100%' }}
                        value={l.fundId}
                        onChange={(v) => patchLine(l.key, { fundId: v || null })}
                        options={candidates.map((f) => ({
                          value: f._id,
                          label: `${f.name}${f.isCentralFund ? ' · HO Bank' : ''}${f.isIBCTCashFund ? ' · Cash Drawer' : ''}`,
                        }))}
                      />
                    </Col>
                  )}
                  {dispatches.length > 0 && !(Number(l.debit) > 0) && !l.autoFor && (
                    <Col flex="320px">
                      <Select
                        size="small" allowClear showSearch optionFilterProp="label"
                        placeholder="Settle a smelter dispatch (optional)"
                        style={{ width: '100%' }}
                        value={l.smelterDispatchId}
                        onChange={(v) => syncSmelterLines(l.key, {
                          dispatchId: v || null,
                          credit: v ? dispatchById[v]?.availableToSettle ?? null : null,
                          tds: null,
                        })}
                        options={dispatches.map((d) => ({
                          value: d._id,
                          label: `${d.dispatchId} · ${d.smelterName} · ₹${fmt(d.availableToSettle)} ${d.isSettled ? 'GST due' : 'due'}`,
                        }))}
                      />
                    </Col>
                  )}
                </Row>

                {/* Smelter settlement — what this line clears, and the TDS inside it */}
                {l.smelterDispatchId && (() => {
                  const d = dispatchById[l.smelterDispatchId];
                  if (!d) return null;
                  const credit = Number(l.credit) || 0;
                  const tds = Math.min(Number(l.smelterTdsAmount) || 0, credit);
                  const afterSettled = r2(d.settled + credit);
                  const settlesNow = afterSettled >= d.metalValue - 0.01;
                  return (
                    <div style={{ marginTop: 8, padding: 8, background: 'rgba(24,144,255,0.05)', borderRadius: 6 }}>
                      <Space size={6} wrap style={{ marginBottom: 6 }}>
                        <Tag color="blue">{d.dispatchId}</Tag>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          {d.smelterName} · invoice {d.invoiceNo} · billed ₹{fmt(d.invoiceTotal)}
                          {d.settled > 0 && <> · already settled ₹{fmt(d.settled)}</>}
                          {' '}· can still take <b>₹{fmt(d.availableToSettle)}</b>
                          {d.pendingInApproval > 0 && (
                            <Tooltip title="Another manual entry for this dispatch is waiting for approval, and its amount is already set aside.">
                              {' '}<InfoCircleOutlined />
                            </Tooltip>
                          )}
                        </Text>
                      </Space>
                      <Row gutter={8} align="middle">
                        <Col flex="200px">
                          <InputNumber
                            size="small" min={0} max={credit || undefined} controls={false}
                            style={{ width: '100%' }}
                            placeholder="of which TDS withheld"
                            value={l.smelterTdsAmount}
                            onChange={(v) => syncSmelterLines(l.key, { tds: v })}
                            formatter={(v) => (v ? `₹ ${v}` : '')}
                          />
                        </Col>
                        <Col flex="auto">
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            {tds > 0
                              ? <>₹{fmt(r2(credit - tds))} cash + ₹{fmt(tds)} TDS clears ₹{fmt(credit)} of the bill.</>
                              : <>Enter the TDS the smelter withheld, if any — a debit line is added for it.</>}
                            {' '}
                            {d.isSettled
                              ? <>Already settled at its metal value — this clears GST off its debtor balance.</>
                              : settlesNow
                                ? <Text type="success" style={{ fontSize: 12 }}>Settles the dispatch on approval ✓</Text>
                                : <>₹{fmt(r2(Math.max(0, d.metalValue - afterSettled)))} would still be due after this.</>}
                          </Text>
                        </Col>
                      </Row>
                      <Text type="secondary" style={{ fontSize: 11, display: 'block', marginTop: 6 }}>
                        <InfoCircleOutlined /> On approval this entry is registered as a payment receipt against
                        {' '}{d.dispatchId} — no second journal entry is posted. Credit the fund the money landed in
                        using “Sync a fund” on the debit line.
                      </Text>
                    </div>
                  );
                })()}

                {/* Fund-aware extra inputs */}
                {fund && (
                  <div style={{ marginTop: 8, padding: 8, background: 'rgba(0,0,0,0.02)', borderRadius: 6 }}>
                    <Space size={6} wrap>
                      <Tag color={isInflow ? 'green' : 'volcano'}>{isInflow ? 'Fund balance ↑ (money in)' : 'Fund balance ↓ (money out)'}</Tag>
                      <Text type="secondary" style={{ fontSize: 12 }}>{fund.name} (linked {fund.linkedAccountCode})</Text>
                    </Space>

                    {fund.isIBCTCashFund && (
                      <div style={{ marginTop: 6 }}>
                        <Space>
                          <WalletOutlined />
                          <Text style={{ fontSize: 12 }}>Cash drawer — note breakdown required. Counted: <b>₹{fmt(denomTotal(l.denominations))}</b> / needed ₹{fmt(amount)}</Text>
                          <Button size="small" onClick={() => setDenomEditor({ key: l.key })}>Edit notes</Button>
                        </Space>
                      </div>
                    )}

                    {fund.isCentralFund && isInflow && (
                      <Row gutter={8} style={{ marginTop: 6 }}>
                        <Col span={8}><Input size="small" placeholder="Bank account" value={l.bankAccount} onChange={(e) => patchLine(l.key, { bankAccount: e.target.value })} prefix={<BankOutlined />} /></Col>
                        <Col span={8}><Input size="small" placeholder="Bank reference" value={l.bankReference} onChange={(e) => patchLine(l.key, { bankReference: e.target.value })} /></Col>
                        <Col span={8}><DatePicker size="small" placeholder="Value date" style={{ width: '100%' }} value={l.valueDate} onChange={(v) => patchLine(l.key, { valueDate: v })} format="DD/MM/YYYY" /></Col>
                      </Row>
                    )}
                    {fund.isCentralFund && !isInflow && (
                      <div style={{ marginTop: 6 }}>
                        <Text type="secondary" style={{ fontSize: 12 }}><InfoCircleOutlined /> Records a Central-Fund ledger adjustment (HO bank balance ↓).</Text>
                      </div>
                    )}
                  </div>
                )}

                {issues.length > 0 && (
                  <div style={{ marginTop: 6 }}>
                    {issues.map((iss, i) => <Tag key={i} color="error" style={{ fontSize: 11 }}>{iss}</Tag>)}
                  </div>
                )}
              </Card>
            );
          })}
        </Space>

        <Button type="dashed" icon={<PlusOutlined />} onClick={addLine} style={{ marginTop: 10 }} block>Add line</Button>

        <Divider style={{ margin: '12px 0' }} />
        <Row justify="end" gutter={24}>
          <Col><Text type="secondary">Total Debit</Text><br /><Title level={5} style={{ margin: 0 }}>₹{fmt(totalDebit)}</Title></Col>
          <Col><Text type="secondary">Total Credit</Text><br /><Title level={5} style={{ margin: 0 }}>₹{fmt(totalCredit)}</Title></Col>
          <Col>
            <Text type="secondary">Difference</Text><br />
            <Title level={5} style={{ margin: 0, color: balanced ? '#52c41a' : '#ff4d4f' }}>₹{fmt(diff)}</Title>
          </Col>
        </Row>
        {!balanced && totalDebit + totalCredit > 0 && (
          <Alert style={{ marginTop: 8 }} type="warning" showIcon message="Debits must equal credits before you can submit." />
        )}
      </Modal>

      {/* Denomination editor for a cash-drawer line */}
      <Modal
        open={!!denomEditor}
        title="Cash Drawer — Note Breakdown"
        onCancel={() => setDenomEditor(null)}
        onOk={() => setDenomEditor(null)}
        okText="Done"
        width={520}
        destroyOnClose
      >
        {denomEditor && (() => {
          const line = lines.find((l) => l.key === denomEditor.key);
          const amount = (Number(line?.debit) || 0) || (Number(line?.credit) || 0);
          return (
            <>
              <DenominationCountForm
                counts={line?.denominations || {}}
                onChange={(c) => patchLine(denomEditor.key, { denominations: c })}
              />
              <Divider style={{ margin: '10px 0' }} />
              <Row justify="space-between">
                <Text>Counted: <b>₹{fmt(denomTotal(line?.denominations))}</b></Text>
                <Text>Line amount: <b>₹{fmt(amount)}</b></Text>
              </Row>
            </>
          );
        })()}
      </Modal>
    </>
  );
}
